import React from 'react';

interface LesionAssessmentProps {
  data: {
    type: string;
    size_mm: number | null;
    color: string;
    border: string;
    surface: string;
    distribution: string;
    anatomic_location: string;
    number_of_lesions: string;
    symptom: string;
  };
  onChange: (field: string, value: any) => void;
}

const TYPE_OPTIONS = [
  { value: '', label: 'Seleccionar...' },
  { value: 'mácula', label: 'Mácula' },
  { value: 'pápula', label: 'Pápula' },
  { value: 'placa', label: 'Placa' },
  { value: 'nódulo', label: 'Nódulo' },
  { value: 'vesícula', label: 'Vesícula' },
  { value: 'ampolla', label: 'Ampolla' },
  { value: 'pústula', label: 'Pústula' },
  { value: 'urticaria', label: 'Urticaria' },
  { value: 'escama', label: 'Escama' },
  { value: 'costra', label: 'Costra' },
  { value: 'erosión', label: 'Erosión' },
  { value: 'úlcera', label: 'Úlcera' },
  { value: 'fisura', label: 'Fisura' },
  { value: 'queratosis', label: 'Queratosis' },
];

const COLOR_OPTIONS = [
  { value: '', label: 'Seleccionar...' },
  { value: 'eritematosa', label: 'Eritematosa' },
  { value: 'hiperpigmentada', label: 'Hiperpigmentada' },
  { value: 'hipopigmentada', label: 'Hipopigmentada' },
  { value: 'violácea', label: 'Violácea' },
  { value: 'amelanótica', label: 'Amelanótica' },
  { value: 'purpúrica', label: 'Purpúrica' },
  { value: 'amarillenta', label: 'Amarillenta' },
];

const BORDER_OPTIONS = [
  { value: '', label: 'Seleccionar...' },
  { value: 'regular', label: 'Regular' },
  { value: 'irregular', label: 'Irregular' },
  { value: 'mal definido', label: 'Mal Definido' },
  { value: 'solevado', label: 'Solevado' },
];

const SURFACE_OPTIONS = [
  { value: '', label: 'Seleccionar...' },
  { value: 'lisa', label: 'Lisa' },
  { value: 'rugosa', label: 'Rugosa' },
  { value: 'exofítica', label: 'Exofítica' },
  { value: 'umbilicada', label: 'Umbilicada' },
  { value: 'verrugosa', label: 'Verrugosa' },
  { value: 'descamativa', label: 'Descamativa' },
];

const DISTRIBUTION_OPTIONS = [
  { value: '', label: 'Seleccionar...' },
  { value: 'localizada', label: 'Localizada' },
  { value: 'generalizada', label: 'Generalizada' },
  { value: 'simétrica', label: 'Simétrica' },
  { value: 'asimétrica', label: 'Asimétrica' },
  { value: 'dermatomal', label: 'Dermatomal' },
  { value: 'fotosxpuesta', label: 'Fotoexpuesta' },
  { value: 'intertriginosa', label: 'Intertriginosa' },
];

const NUMBER_OPTIONS = [
  { value: '', label: 'Seleccionar...' },
  { value: 'única', label: 'Única' },
  { value: 'múltiples (<10)', label: 'Múltiples (<10)' },
  { value: 'múltiples (10-50)', label: 'Múltiples (10-50)' },
  { value: 'múltiples (>50)', label: 'Múltiples (>50)' },
];

const SYMPTOM_OPTIONS = [
  { value: '', label: 'Seleccionar...' },
  { value: 'asintomática', label: 'Asintomática' },
  { value: 'prurito', label: 'Prurito' },
  { value: 'dolor', label: 'Dolor' },
  { value: 'ardor', label: 'Ardor' },
  { value: 'parestesia', label: 'Parestesia' },
];

const LesionAssessment: React.FC<LesionAssessmentProps> = ({ data, onChange }) => {
  return (
    <div className="card mb-3">
      <div className="card-body">
        <h5 className="card-title">
          <i className="fa fa-eye me-1"></i>
          Evaluación de la Lesión
        </h5>
        <div className="row mt-3">
          <div className="col-md-4">
            <div className="mb-3">
              <label className="form-label">Tipo de Lesión</label>
              <select
                className="form-select"
                value={data.type}
                onChange={e => onChange('type', e.target.value)}
              >
                {TYPE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-md-2">
            <div className="mb-3">
              <label className="form-label">Tamaño (mm)</label>
              <input
                type="number"
                className="form-control"
                min={0}
                value={data.size_mm ?? ''}
                onChange={e => onChange('size_mm', e.target.value ? Number(e.target.value) : null)}
                placeholder="mm"
              />
            </div>
          </div>
          <div className="col-md-3">
            <div className="mb-3">
              <label className="form-label">Color</label>
              <select
                className="form-select"
                value={data.color}
                onChange={e => onChange('color', e.target.value)}
              >
                {COLOR_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-md-3">
            <div className="mb-3">
              <label className="form-label">Borde</label>
              <select
                className="form-select"
                value={data.border}
                onChange={e => onChange('border', e.target.value)}
              >
                {BORDER_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-3">
            <div className="mb-3">
              <label className="form-label">Superficie</label>
              <select
                className="form-select"
                value={data.surface}
                onChange={e => onChange('surface', e.target.value)}
              >
                {SURFACE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-md-3">
            <div className="mb-3">
              <label className="form-label">Distribución</label>
              <select
                className="form-select"
                value={data.distribution}
                onChange={e => onChange('distribution', e.target.value)}
              >
                {DISTRIBUTION_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-md-3">
            <div className="mb-3">
              <label className="form-label">Número de Lesiones</label>
              <select
                className="form-select"
                value={data.number_of_lesions}
                onChange={e => onChange('number_of_lesions', e.target.value)}
              >
                {NUMBER_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-md-3">
            <div className="mb-3">
              <label className="form-label">Síntoma</label>
              <select
                className="form-select"
                value={data.symptom}
                onChange={e => onChange('symptom', e.target.value)}
              >
                {SYMPTOM_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-12">
            <div className="mb-3">
              <label className="form-label">Localización Anatómica</label>
              <input
                type="text"
                className="form-control"
                value={data.anatomic_location}
                onChange={e => onChange('anatomic_location', e.target.value)}
                placeholder="Describa la ubicación anatómica de la(s) lesión(es)..."
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LesionAssessment;
